<template>
  <svg v-if="mounted" class="preview" :x="x" :y="y" :width="width" :height="height" :viewBox="viewBox" xmlns="http://www.w3.org/2000/svg">
    <!-- Mounted artwork component -->
    <RenderSvgMountedArtwork
      :frameSpec="frameSpec"
      :x="mouldingRearWidth"
      :y="mouldingRearWidth"
      :allowUpload="allowUpload"
      @upload-click="$emit('upload-click')"
    />

    <!-- Acrylic component -->
    <RenderSvgAcrylic
      :frameSpec="frameSpec"
      :x="mouldingRearWidth"
      :y="mouldingRearWidth"
    />

    <!-- Moulding component -->
    <RenderSvgMoulding
      v-if="moulding"
      :frameSpec="frameSpec"
      :moulding="moulding"
      :x="0"
      :y="0"
    />
  </svg>
</template>

<script>
import { getMouldingInfo } from "../../framingUtilities";
import spreeClient from "../../spree";
import RenderSvgMountedArtwork from './RenderSvgMountedArtwork.vue';
import RenderSvgAcrylic from './RenderSvgAcrylic.vue';
import RenderSvgMoulding from './RenderSvgMoulding.vue';
import isEmpty from "lodash/isEmpty";

export default {
  components: {
    RenderSvgMountedArtwork,
    RenderSvgAcrylic,
    RenderSvgMoulding
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '100%'
    },
    allowUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    focusOn: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      moulding: null,
      mounted: false,
      spree: null
    }
  },
  watch: {
    frameSpec: {
      handler: async function () {
        this.moulding = await getMouldingInfo(this.frameSpec.moulding.permalink, this.spree);

        if (!this.frameSpec.getArtwork().exterior.artworkOpeningWidthInInches || !this.frameSpec.getArtwork().exterior.artworkOpeningHeightInInches) {
          this.frameSpec.setArtworkOpeningDimensions();
        }
      },
      deep: true
    }
  },
  async mounted () {
    this.spree = new spreeClient(this.$config && this.$config.joineryHost ? this.$config.joineryHost : "/");
    this.moulding = await getMouldingInfo(this.frameSpec.moulding.permalink, this.spree);
    this.mounted = true;
  },
  computed: {
    artwork() {
      return this.frameSpec.getArtwork(0);
    },
    mat() {
      return this.frameSpec.mats[0] || {};
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvas();
    },
    totalsOfAllMatRevealsOrMountReveals() {
      if (!isEmpty(this.mat)) {
        return {
          topInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
          rightInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
          bottomInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
          leftInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
        };
      } else if (this.frameSpec.isFloated() || this.frameSpec.isClearFloated()) {
        return this.frameSpec.mount.reveal;
      } else {
        return {
          topInInches: 0,
          rightInInches: 0,
          bottomInInches: 0,
          leftInInches: 0
        }
      }
    },
    faceWidth() {
      // The moulding tile images for canvas mouldings include the canvas gap width in the image.
      const canvasGapWidth = this.moulding.canvas_gap_width || 0;
      return this.moulding.face_width + canvasGapWidth;
    },
    mouldingRearWidth() {
      if (this.isCanvasMoulding) {
        return this.faceWidth || 0;
      } else {
        return (this.moulding && this.moulding.rear_width) || 0;
      }
    },
    isMobile() {
      if (typeof window !== 'undefined') {
        return window.innerWidth < 832;        
      } else {
        return false;
      }
    },
    viewBox() {
      if (!this.artwork) return '0 0 0 0';
      const width = this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsOrMountReveals.leftInInches + this.totalsOfAllMatRevealsOrMountReveals.rightInInches + 2 * this.mouldingRearWidth;
      const height = this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsOrMountReveals.topInInches + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 2 * this.mouldingRearWidth;

      if (this.focusOn === 'moulding-plate') {
        const plateWidth = 2.36;
        const boxWidth = plateWidth + 0.5;

        let boxHeight;
        if (this.isMobile) {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 2;
        } else {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 0.5;
        }

        let boxY;
        if (this.isMobile) {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 2.25;
        } else {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 0.25;
        }
        
        const boxX = (width / 2) - (boxWidth / 2);

        return `${boxX} ${boxY} ${boxWidth} ${boxHeight}`;
      } else if (this.focusOn === 'mat-caption') {
        const matCaptionWidth = 2.5;
        const boxWidth = matCaptionWidth + 0.5;

        let boxHeight;
        if (this.isMobile) {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 1.25;
        } else {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 2;
        }

        let boxY;
        if (this.isMobile) {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 0.625;
        } else {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 1;
        }

        const matCaptionAlignment = this.mat.matCaption.fontAlignment;
        let boxX;

        if (matCaptionAlignment === "left") {
          boxX = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.leftInInches - 1;
        } else if (matCaptionAlignment === "right") {
          boxX = width - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.rightInInches - boxWidth + 1;
        } else {
          boxX = (width / 2) - (boxWidth / 2);
        }

        return `${boxX} ${boxY} ${boxWidth} ${boxHeight}`;
      } else {
        return `0 0 ${width} ${height}`;
      }
    }
  }
};
</script>

<style lang="scss">
  .preview {
    max-height: 100%;
  }
</style>